import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Form, Table } from "react-bootstrap";
import { FaWarehouse, FaExclamationTriangle, FaTrashAlt, FaCoins } from "react-icons/fa";
import { api } from "../../config/api";
import { format, subDays } from "date-fns";
import DateRangeSelector from "./DateRangeSelector";

export default function InventoryReport() {
  const [wastageData, setWastageData] = useState([]);
  const [valuationData, setValuationData] = useState([]);
  const [lowStockData, setLowStockData] = useState({ lowStock: [], outOfStock: [] });
  const [searchWastageItem, setSearchWastageItem] = useState("");
  const [dateRange, setDateRange] = useState({
    startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd')
  });
  const [loading, setLoading] = useState(false);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const qs = `?startDate=${dateRange.startDate} 00:00:00&endDate=${dateRange.endDate} 23:59:59`;
      const [wastageRes, valRes, lowRes] = await Promise.all([
        api.get(`/api/reports/wastage${qs}`, { headers }),
        api.get(`/api/reports/sunk-cost`, { headers }),
        api.get(`/api/reports/low-stock`, { headers })
      ]);
      
      setWastageData(wastageRes.data);
      setValuationData(valRes.data);
      setLowStockData(lowRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [dateRange]);

  const totalWastageCost = wastageData.reduce((acc, curr) => acc + Number(curr.cost), 0);
  const totalValuationCost = valuationData.reduce((acc, curr) => acc + Number(curr.total_value), 0);
  
  const parsedWastageData = wastageData.map(item => {
    const match = item.description.match(/^ปรับลบสต๊อก:\s+(.*?)\s+\((.*?)\)\s+-\s+(.*)$/);
    const isParsed = !!match;
    return {
      ...item,
      itemName: isParsed ? match[1] : "-",
      qty: isParsed ? match[2] : "-",
      reason: isParsed ? match[3] : item.description,
      isParsed
    };
  });

  const filteredWastageData = searchWastageItem
    ? parsedWastageData.filter(item => item.itemName.toLowerCase().includes(searchWastageItem.toLowerCase()))
    : parsedWastageData;

  const directCostItems = valuationData.filter(item => item.type === "direct_cost");
  const sunkCostItems = valuationData.filter(item => item.type === "sunk_cost");
  const unclassifiedItems = valuationData.filter(item => item.type !== "direct_cost" && item.type !== "sunk_cost");

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="fw-bold mb-0" style={{ color: "#1e3c72" }}>
          <FaWarehouse className="me-2" /> Inventory & Stock Reports
        </h4>
        <div className="d-flex gap-2">
          <DateRangeSelector dateRange={dateRange} onChange={setDateRange} />
        </div>
      </div>

      <Row className="g-4 mb-4">
        <Col md={6}>
          <Card className="border-0 shadow-sm text-center h-100" style={{ borderRadius: '12px' }}>
            <Card.Body className="d-flex flex-column justify-content-center">
              <div className="mb-2 text-danger"><FaTrashAlt size={24}/></div>
              <h6 className="text-uppercase mb-2 text-muted">Total Wastage Cost (Selected Period)</h6>
              <h3 className="fw-bold mb-0 text-danger">฿{totalWastageCost.toLocaleString(undefined, {minimumFractionDigits: 2})}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="border-0 shadow-sm text-center h-100" style={{ borderRadius: '12px', background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', color: 'white' }}>
            <Card.Body className="d-flex flex-column justify-content-center">
              <div className="mb-2"><FaCoins size={24}/></div>
              <h6 className="text-uppercase mb-2 opacity-75">Total Inventory Value (All Items)</h6>
              <h3 className="fw-bold mb-0">฿{totalValuationCost.toLocaleString(undefined, {minimumFractionDigits: 2})}</h3>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-4 mb-4">
        <Col lg={7}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <Card.Body>
              <h6 className="fw-bold text-muted mb-3"><FaCoins className="me-2"/>Inventory Valuation by Item</h6>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <Table responsive hover className="align-middle mb-0">
                  <thead className="table-light sticky-top">
                    <tr>
                      <th className="border-0 rounded-start">Item Name</th>
                      <th className="border-0 text-center">Remaining Stock</th>
                      <th className="border-0 text-end rounded-end">Total Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {directCostItems.length > 0 && (
                      <tr>
                        <td colSpan="3" className="bg-light fw-bold text-primary py-2">
                          Direct Cost Items
                        </td>
                      </tr>
                    )}
                    {directCostItems.map((item, idx) => (
                      <tr key={`direct-${idx}`}>
                        <td className="fw-medium text-dark ps-4">{item.name}</td>
                        <td className="text-center">{Number(item.remaining_quantity).toLocaleString()} {item.unit}</td>
                        <td className="text-end fw-bold text-success">฿{Number(item.total_value).toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                      </tr>
                    ))}
                    
                    {sunkCostItems.length > 0 && (
                      <tr>
                        <td colSpan="3" className="bg-light fw-bold text-warning py-2">
                          Sunk Cost Items
                        </td>
                      </tr>
                    )}
                    {sunkCostItems.map((item, idx) => (
                      <tr key={`sunk-${idx}`}>
                        <td className="fw-medium text-dark ps-4">{item.name}</td>
                        <td className="text-center">{Number(item.remaining_quantity).toLocaleString()} {item.unit}</td>
                        <td className="text-end fw-bold text-warning">฿{Number(item.total_value).toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                      </tr>
                    ))}

                    {unclassifiedItems.length > 0 && (
                      <tr>
                        <td colSpan="3" className="bg-light fw-bold text-muted py-2">
                          Other Items
                        </td>
                      </tr>
                    )}
                    {unclassifiedItems.map((item, idx) => (
                      <tr key={`other-${idx}`}>
                        <td className="fw-medium text-dark ps-4">{item.name}</td>
                        <td className="text-center">{Number(item.remaining_quantity).toLocaleString()} {item.unit}</td>
                        <td className="text-end fw-bold text-secondary">฿{Number(item.total_value).toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                      </tr>
                    ))}

                    {valuationData.length === 0 && <tr><td colSpan="3" className="text-center text-muted py-3">No inventory data</td></tr>}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={5}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '12px', borderLeft: '4px solid #e74c3c !important' }}>
            <Card.Body>
              <h6 className="fw-bold text-muted mb-3"><FaExclamationTriangle className="me-2 text-warning"/>Low Stock Alerts</h6>
              
              {lowStockData.outOfStock.length > 0 && (
                <div className="mb-4">
                  <h6 className="text-danger fw-bold small text-uppercase">Out of Stock</h6>
                  <ul className="list-group list-group-flush">
                    {lowStockData.outOfStock.map((item, idx) => (
                      <li key={idx} className="list-group-item d-flex justify-content-between align-items-center px-0">
                        <span className="fw-medium text-dark">{item.name}</span>
                        <span className="badge bg-danger rounded-pill">0 {item.unit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <h6 className="text-warning fw-bold small text-uppercase">Running Low</h6>
                <ul className="list-group list-group-flush">
                  {lowStockData.lowStock.map((item, idx) => (
                    <li key={idx} className="list-group-item d-flex justify-content-between align-items-center px-0">
                      <span className="fw-medium text-dark">{item.name}</span>
                      <span className="badge bg-warning text-dark rounded-pill">{item.stock_quantity} {item.unit}</span>
                    </li>
                  ))}
                  {lowStockData.lowStock.length === 0 && <li className="list-group-item px-0 text-muted">No items are running low.</li>}
                </ul>
              </div>

            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-4">
        <Col lg={12}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold text-muted mb-0"><FaTrashAlt className="me-2"/>Wastage & Adjustments Log</h6>
                <div style={{ width: '250px' }}>
                  <Form.Select 
                    size="sm" 
                    value={searchWastageItem} 
                    onChange={(e) => setSearchWastageItem(e.target.value)}
                  >
                    <option value="">All Items</option>
                    {[...new Set(parsedWastageData.filter(i => i.isParsed).map(i => i.itemName))].map((name, idx) => (
                      <option key={idx} value={name}>{name}</option>
                    ))}
                  </Form.Select>
                </div>
              </div>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                <Table responsive hover className="align-middle mb-0">
                  <thead className="table-light sticky-top">
                    <tr>
                      <th className="border-0 rounded-start">Date</th>
                      <th className="border-0">Item</th>
                      <th className="border-0 text-center">Qty</th>
                      <th className="border-0">Reason / Description</th>
                      <th className="border-0 text-end rounded-end">Estimated Cost Loss</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredWastageData.map((item, idx) => (
                      <tr key={idx}>
                        <td className="text-muted">{format(new Date(item.created_at), 'dd MMM yyyy, HH:mm')}</td>
                        <td className="fw-medium text-dark">{item.itemName}</td>
                        <td className="text-center">{item.qty}</td>
                        <td className="text-muted">{item.reason}</td>
                        <td className="text-end fw-bold text-danger">-฿{Number(item.cost).toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                      </tr>
                    ))}
                    {filteredWastageData.length === 0 && <tr><td colSpan="5" className="text-center text-muted py-3">No wastage recorded for this selection.</td></tr>}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
